import type {
  ConversationEntry,
  CustomerRecord,
  FutureReadinessSignal,
} from '@/domain/types';

export interface CallExtraction {
  summary: string;
  suggestedActions: NonNullable<ConversationEntry['suggestedActions']>;
  futureSignals: Array<Pick<FutureReadinessSignal, 'rawPhrase' | 'scheduledFor'>>;
  rapportNotes: string[];
}

export interface MessageExtraction {
  aiAnnotation?: string;
  suggestedActions: NonNullable<ConversationEntry['suggestedActions']>;
}

// Phrases like "contact me at 3pm", "call me back at 11", "ring me Tuesday",
// "call me back any time after 8", "hit me up after 5".
const TIME_HINT = /(?:contact|call|ring|hit)\s+me(?:\s+back|\s+up)?(?:\s+any\s*time)?\s+(?:at|on|next|after|before)\s+([^.,!?]+?)(?=[.,!?"]|$)/i;
// Phrases like "next week", "next month", "in a few weeks", "after my holiday",
// "tomorrow morning", "after summer", "let you know tomorrow".
const FUTURE_HINT = /\b(?:next\s+(?:week|month|year)|after\s+(?:my\s+)?(?:holiday|trip|summer|move)|in\s+(?:a\s+)?(?:few|couple|two|three)\s+(?:weeks|months)|tomorrow(?:\s+(?:morning|afternoon|evening))?)\b[^.!?]*/i;
const DEPOSIT_HINT = /\b(?:send|share|drop|paste).{0,15}(?:link|deposit|stripe|payment)|i(?:'| a)?m? ready to pay|i want to pay\b/i;
const RESCHEDULE_HINT = /\b(?:reschedule|move my (?:session|booking|appointment)|push back|another day|different (?:day|time))\b/i;
const BOOK_HINT = /\b(?:book me|lock (?:it|me) in|i(?:'| wa)?ll take (?:that|it)|saturday\s+works|sunday\s+works)\b/i;

// Crude offset from a wall-clock time-of-day fragment into a near-future ISO.
// Used purely for prototype demo; just adds a day if it's already past.
function nextOccurrence(fragment: string, fromIso: string): string {
  const match = fragment.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  const base = new Date(fromIso);
  if (!match) {
    base.setDate(base.getDate() + 1);
    return base.toISOString();
  }
  let hour = parseInt(match[1] ?? '9', 10);
  const minute = parseInt(match[2] ?? '0', 10);
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === 'pm' && hour < 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;
  const target = new Date(base);
  target.setHours(hour, minute, 0, 0);
  if (target.getTime() <= base.getTime()) {
    target.setDate(target.getDate() + 1);
  }
  return target.toISOString();
}

function uniqueId(seed: string) {
  return `${seed}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

export function extractFromCall(
  transcript: Array<{ speaker: string; body: string }>,
  nowIso: string,
): CallExtraction {
  const customerSaid = transcript
    .filter((t) => t.speaker === 'customer')
    .map((t) => t.body)
    .join(' ');
  const fullText = transcript.map((t) => t.body).join(' ');
  const suggestedActions: NonNullable<ConversationEntry['suggestedActions']> = [];
  const futureSignals: CallExtraction['futureSignals'] = [];
  const rapportNotes: string[] = [];

  const timeMatch = customerSaid.match(TIME_HINT);
  if (timeMatch) {
    const phrase = timeMatch[0];
    const triggerDate = nextOccurrence(timeMatch[1] ?? '', nowIso);
    futureSignals.push({ rawPhrase: phrase, scheduledFor: triggerDate });
    suggestedActions.push({
      id: uniqueId('act-future'),
      label: `Tag future-readiness: ${phrase}`,
      kind: 'future-readiness',
      payload: { triggerDate, rawPhrase: phrase },
    });
  }

  const futureMatch = customerSaid.match(FUTURE_HINT);
  if (futureMatch && !timeMatch) {
    const phrase = futureMatch[0];
    const triggerDate = (() => {
      const d = new Date(nowIso);
      d.setDate(d.getDate() + 28);
      return d.toISOString();
    })();
    futureSignals.push({ rawPhrase: phrase, scheduledFor: triggerDate });
    suggestedActions.push({
      id: uniqueId('act-future'),
      label: `Tag future-readiness: ${phrase}`,
      kind: 'future-readiness',
      payload: { triggerDate, rawPhrase: phrase },
    });
  }

  if (DEPOSIT_HINT.test(customerSaid)) {
    suggestedActions.push({
      id: uniqueId('act-deposit'),
      label: 'Send deposit link',
      kind: 'deposit-link',
    });
  }
  if (RESCHEDULE_HINT.test(customerSaid)) {
    suggestedActions.push({
      id: uniqueId('act-reschedule'),
      label: 'Open reschedule',
      kind: 'reschedule',
    });
  }
  if (BOOK_HINT.test(customerSaid)) {
    suggestedActions.push({
      id: uniqueId('act-self-book'),
      label: 'Send self-book link',
      kind: 'self-book',
    });
  }

  // Lift a short rapport note out of the customer's first volunteered detail.
  const firstCustomerLine = transcript.find((t) => t.speaker === 'customer')?.body;
  if (firstCustomerLine && firstCustomerLine.length < 140) {
    rapportNotes.push(firstCustomerLine);
    suggestedActions.push({
      id: uniqueId('act-rapport'),
      label: 'Save rapport note',
      kind: 'rapport-note',
      payload: { note: firstCustomerLine },
    });
  }

  const summary = buildSummary(transcript, suggestedActions, fullText);
  return { summary, suggestedActions, futureSignals, rapportNotes };
}

function buildSummary(
  transcript: Array<{ speaker: string; body: string }>,
  actions: NonNullable<ConversationEntry['suggestedActions']>,
  fullText: string,
): string {
  if (transcript.length === 0) return 'Call ended with no transcript captured.';
  const actionBits = actions
    .map((a) => {
      if (a.kind === 'future-readiness') return 'future-readiness hint';
      if (a.kind === 'deposit-link') return 'deposit ask';
      if (a.kind === 'reschedule') return 'reschedule request';
      if (a.kind === 'self-book') return 'booking intent';
      return 'rapport';
    })
    .filter((b, i, arr) => arr.indexOf(b) === i);
  const tone = /thanks|appreciate|love|great/i.test(fullText)
    ? 'Warm tone.'
    : /no|cancel|reschedule/i.test(fullText)
      ? 'Hesitant tone.'
      : 'Neutral tone.';
  return `${tone} Detected: ${actionBits.length > 0 ? actionBits.join(', ') : 'no specific action'}.`;
}

export type RecommendedChannel = 'voice' | 'sms' | 'meta-dm' | 'ig-dm' | 'email';

export interface ChannelRecommendation {
  channel: RecommendedChannel;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
}

const CHANNEL_LABEL: Record<RecommendedChannel, string> = {
  voice: 'voice',
  sms: 'SMS',
  'meta-dm': 'Messenger',
  'ig-dm': 'IG DM',
  email: 'email',
};

export function recommendChannel(customer: CustomerRecord): ChannelRecommendation {
  const scores: Record<RecommendedChannel, number> = {
    voice: 0,
    sms: 0,
    'meta-dm': 0,
    'ig-dm': 0,
    email: 0,
  };
  const reasons: string[] = [];

  // Most recent inbound channel is the strongest signal. They reached out that way.
  const lastInbound = [...customer.conversation]
    .reverse()
    .find((e) => e.direction === 'in');
  if (lastInbound) {
    const ch = lastInbound.channel;
    if (ch === 'voice') {
      scores.voice += 25;
      reasons.push('previous voice contact');
    } else if (ch === 'sms' || ch === 'meta-dm' || ch === 'ig-dm' || ch === 'email') {
      scores[ch] += 40;
      reasons.push(`replied last on ${CHANNEL_LABEL[ch]}`);
    }
  }

  // Demographics: younger skews DM, older skews voice.
  if (customer.ageBand === '18-24') {
    scores['ig-dm'] += 20;
    scores.sms += 10;
    reasons.push(`${customer.ageBand} responds to DM`);
  } else if (customer.ageBand === '25-34') {
    scores.sms += 15;
    scores['ig-dm'] += 10;
    reasons.push(`${customer.ageBand} responds to text`);
  } else if (customer.ageBand === '35-44') {
    scores.sms += 10;
    scores.voice += 10;
  } else if (customer.ageBand === '45-54' || customer.ageBand === '55+') {
    scores.voice += 20;
    reasons.push(`${customer.ageBand} prefers voice`);
  }

  // Tone tag.
  if (customer.toneTag === 'casual-young') {
    scores['ig-dm'] += 10;
    scores.sms += 5;
  } else if (customer.toneTag === 'professional') {
    scores.email += 8;
    scores.voice += 5;
  } else if (customer.toneTag === 'reserved') {
    scores.sms += 12;
    reasons.push('reserved tone, text first');
  } else if (customer.toneTag === 'warm-familiar') {
    scores.voice += 10;
    reasons.push('warm rapport, call works');
  }

  // Engagement: dormant clients respond better to a soft text than a cold call.
  if (customer.engagementStatus === 'dormant') {
    scores.sms += 10;
    scores['ig-dm'] += 5;
    reasons.push('dormant, soft approach');
  } else if (customer.engagementStatus === 'active') {
    scores.voice += 5;
  }

  const sorted = (Object.entries(scores) as Array<[RecommendedChannel, number]>).sort(
    (a, b) => b[1] - a[1],
  );
  const top = sorted[0]!;
  const second = sorted[1]!;
  const gap = top[1] - second[1];
  const confidence: ChannelRecommendation['confidence'] =
    gap >= 15 ? 'high' : gap >= 5 ? 'medium' : 'low';

  return {
    channel: top[0],
    reason: reasons.slice(0, 2).join(', ') || 'default channel',
    confidence,
  };
}

export function extractFromMessage(body: string, seed: string): MessageExtraction {
  const actions: NonNullable<ConversationEntry['suggestedActions']> = [];
  if (DEPOSIT_HINT.test(body)) {
    actions.push({
      id: `${seed}-deposit`,
      label: 'Send deposit link',
      kind: 'deposit-link',
    });
  }
  if (RESCHEDULE_HINT.test(body)) {
    actions.push({
      id: `${seed}-reschedule`,
      label: 'Open reschedule',
      kind: 'reschedule',
    });
  }
  if (BOOK_HINT.test(body)) {
    actions.push({
      id: `${seed}-self-book`,
      label: 'Send self-book link',
      kind: 'self-book',
    });
  }
  const futureMatch = body.match(FUTURE_HINT) ?? body.match(TIME_HINT);
  if (futureMatch) {
    actions.push({
      id: `${seed}-future`,
      label: `Tag future-readiness: ${futureMatch[0]}`,
      kind: 'future-readiness',
      payload: { rawPhrase: futureMatch[0] },
    });
  }
  const aiAnnotation = actions.length > 0 ? `AI detected: ${actions.map((a) => a.kind).join(', ')}.` : undefined;
  return { aiAnnotation, suggestedActions: actions };
}
